import type { QueryClient } from '@tanstack/react-query';
import { adminConsoleAuthApi, type AdminConsoleUser } from '../api/adminConsole';
import {
  orchestratorAuthApi,
  orchestratorLogoutEverywhere,
  ORCHESTRATOR_AUTH_ME_QUERY_KEY,
  type OrchestratorSessionDto,
} from '../api/orchestratorAuth';
import {
  adminRoute,
  buildLoginReturnQuery,
  isBrowserOnAdminLoginPage,
} from '../config/paths';
import { messageFromOrchestratorAxiosError } from './axiosErrorMessage';
import { clearAdminConsoleToken, getAdminConsoleToken } from './adminConsoleToken';
import { isMfaChallengePendingSession } from './mfaChallengeSession';
import { clearMfaLoginCredentials } from './mfaLoginCredentials';
import { isOidcAdminAuth } from './oidc/oidcConfig';
import { getOidcAccessToken, oidcLogout } from './oidc/oidcSession';
import { clearOrchestratorChainGuard } from './oidc/orchestratorChain';

export {
  isAdminSessionMfaChallenge,
  isMfaChallengePendingSession,
  normalizeAdminMfaRecoveryCode,
} from './mfaChallengeSession';

export const ADMIN_CONSOLE_ME_QUERY_KEY = ['adminConsoleMe'] as const;

export function isOrchestratorDisabled(): boolean {
  return (
    import.meta.env.VITE_ORCHESTRATOR_DISABLED === 'true' ||
    import.meta.env.VITE_ORCHESTRATOR_DISABLED === '1'
  );
}

export type AdminSessionReady = {
  ready: true;
  email: string;
  orchestratorUserId?: string;
  adminUser: AdminConsoleUser;
  orchestratorSession?: OrchestratorSessionDto;
};

export type AdminSessionNotReady = {
  ready: false;
  /**
   * `mfa_challenge` — password login succeeded; MFA step-up pending.
   * Must NOT trigger logout (that wipes `pendingMfaAuth`).
   */
  reason: 'orchestrator' | 'admin-api' | 'both' | 'network' | 'mfa_challenge';
  orchestratorSession?: OrchestratorSessionDto;
};

export type AdminSessionProbe = AdminSessionReady | AdminSessionNotReady;

const unauthenticatedOrchestratorSession = (): OrchestratorSessionDto => ({
  authenticated: false,
});

/** True when the operator session probe failed due to connectivity, not expired credentials. */
export function isAdminSessionNetworkFailure(
  probe: AdminSessionProbe,
): probe is AdminSessionNotReady {
  return !probe.ready && probe.reason === 'network';
}

/**
 * After a failed sign-in step, drop any partial orchestrator cookie / admin JWT so the UI
 * never shows "signed in" alongside a network or provisioning error.
 */
export async function rollbackIncompleteOperatorSignIn(qc?: QueryClient): Promise<void> {
  await terminateOperatorSession(qc);
}

export async function terminateOperatorSession(qc?: QueryClient): Promise<void> {
  adminConsoleAuthApi.logout();
  clearAdminConsoleToken();
  clearMfaLoginCredentials();

  // OIDC mode: dropping the in-memory tokens is the local sign-out; the auth-server session
  // (and the orchestrator cookie, which this mode does not create) are left to `signOutOperator`.
  if (isOidcAdminAuth()) {
    if (qc) qc.removeQueries({ queryKey: ADMIN_CONSOLE_ME_QUERY_KEY });
    return;
  }

  if (!isOrchestratorDisabled()) {
    try {
      await orchestratorLogoutEverywhere();
    } catch {
      /* best effort — local JWT is already cleared */
    }
  }

  if (qc) {
    qc.setQueryData(ORCHESTRATOR_AUTH_ME_QUERY_KEY, unauthenticatedOrchestratorSession());
    qc.removeQueries({ queryKey: ADMIN_CONSOLE_ME_QUERY_KEY });
  }
}

/**
 * The user's explicit "Sign out". Password mode = `terminateOperatorSession` (caller navigates to
 * /login). OIDC mode additionally ends a stray orchestrator cookie session (best effort) and then
 * leaves via auth-server `/oauth/end_session` (revokes refresh tokens, ends the SSO session) —
 * the browser navigates away, so the returned promise never matters to the caller.
 */
export async function signOutOperator(qc?: QueryClient): Promise<void> {
  await terminateOperatorSession(qc);
  if (!isOidcAdminAuth()) return;
  clearOrchestratorChainGuard(); // the next sign-in chains the orchestrator session again
  if (!isOrchestratorDisabled()) {
    try {
      await orchestratorLogoutEverywhere();
    } catch {
      /* best effort */
    }
  }
  oidcLogout();
}

export function redirectToAdminLogin(returnPath?: string): void {
  if (isBrowserOnAdminLoginPage()) return;
  const loginPath = adminRoute('/login');
  const ret = encodeURIComponent(
    returnPath ?? buildLoginReturnQuery(window.location.pathname, window.location.search),
  );
  window.location.assign(`${loginPath}?return=${ret}`);
}

/**
 * Shared 401 handler for identity / income / employer dashboard HTTP clients.
 *
 * Those APIs auth with the admin-api JWT (or EV `ADMIN_JWT_SECRET`), not
 * `orch.sid.op`. A 401 there must not POST orchestrator `/auth/logout` —
 * Application detail prefetches employer footprint, and that 401 was wiping
 * the operator cookie (`cookieNames: []`) while journey calls still returned 200.
 */
export function handleAdminDashboardHttp401(err: unknown): Promise<never> {
  return Promise.reject(err);
}

/**
 * Operator session is valid only when orchestrator (if enabled) and admin-api JWT are both active.
 * Verification dashboards (identity / income / employer) require the admin-api token.
 *
 * Pending MFA (`mfaChallengePending`) is an intentional intermediate state — callers must
 * preserve the orchestrator cookie until verify/recovery or the user abandons sign-in.
 */
export async function probeAdminSession(): Promise<AdminSessionProbe> {
  if (isOidcAdminAuth()) return probeOidcAdminSession();
  const orchestratorDisabled = isOrchestratorDisabled();
  let orchestratorSession: OrchestratorSessionDto | undefined;
  let orchestratorEmail: string | undefined;
  let orchestratorUserId: string | undefined;

  if (!orchestratorDisabled) {
    try {
      orchestratorSession = await orchestratorAuthApi.me();
    } catch {
      return { ready: false, reason: 'network' };
    }
    if (isMfaChallengePendingSession(orchestratorSession)) {
      return {
        ready: false,
        reason: 'mfa_challenge',
        orchestratorSession,
      };
    }
    if (!orchestratorSession?.authenticated) {
      return { ready: false, reason: 'orchestrator', orchestratorSession };
    }
    orchestratorEmail = orchestratorSession.email;
    orchestratorUserId = orchestratorSession.userId;
  }

  if (!getAdminConsoleToken()) {
    return {
      ready: false,
      reason: orchestratorDisabled ? 'admin-api' : 'both',
      orchestratorSession,
    };
  }

  try {
    const adminUser = await adminConsoleAuthApi.me();
    return {
      ready: true,
      email: adminUser.email || orchestratorEmail || '',
      orchestratorUserId,
      adminUser,
      orchestratorSession,
    };
  } catch {
    clearAdminConsoleToken();
    return { ready: false, reason: 'admin-api', orchestratorSession };
  }
}

/**
 * OIDC mode: the admin session is the in-memory auth-server access token + admin-api `/auth/me`.
 * No orchestrator session is required (orchestrator-backed pages still use the orchestrator
 * cookie and degrade without it). 401s are handled by the admin-api client (refresh →
 * re-authorize), so the token is not cleared here.
 */
async function probeOidcAdminSession(): Promise<AdminSessionProbe> {
  if (!getOidcAccessToken()) return { ready: false, reason: 'admin-api' };
  try {
    const adminUser = await adminConsoleAuthApi.me();
    return { ready: true, email: adminUser.email || '', adminUser };
  } catch (err) {
    const ax = err as { response?: { status?: number } };
    return { ready: false, reason: ax.response ? 'admin-api' : 'network' };
  }
}

export function syncAdminSessionToQueryCache(qc: QueryClient, probe: AdminSessionReady): void {
  if (probe.orchestratorSession) {
    qc.setQueryData(ORCHESTRATOR_AUTH_ME_QUERY_KEY, probe.orchestratorSession);
  }
  qc.setQueryData(ADMIN_CONSOLE_ME_QUERY_KEY, probe.adminUser);
}

/** Signup rejected because the email is already registered (not a session cookie conflict). */
export function isSignupAccountExistsError(err: unknown): boolean {
  const ax = err as { response?: { status?: number; data?: unknown } };
  if (ax.response?.status !== 409) return false;

  const data = ax.response?.data;
  if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>;
    if (o.accountExists === true) return true;
    const combined = [o.message, o.error]
      .filter((x): x is string => typeof x === 'string')
      .join(' ');
    if (/already exists/i.test(combined)) return true;
  }

  const fromBody = messageFromOrchestratorAxiosError(err);
  if (/already exists/i.test(fromBody)) return true;

  // Signup conflicts should not be mistaken for "already signed in" session errors.
  return !/already signed in/i.test(fromBody);
}

export function isOrchestratorAlreadySignedInError(err: unknown): boolean {
  if (isSignupAccountExistsError(err)) return false;

  const fromBody = messageFromOrchestratorAxiosError(err);
  if (/already signed in/i.test(fromBody)) return true;

  const ax = err as { response?: { status?: number } };
  if (ax.response?.status === 409 && /already signed in/i.test(fromBody)) return true;

  const msg =
    err instanceof Error
      ? err.message
      : typeof err === 'object' && err && 'message' in err
        ? String((err as { message?: unknown }).message ?? '')
        : String(err ?? '');
  return /already signed in/i.test(msg);
}
